import { Injectable, OnDestroy } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Observable, throwError, Subscription } from "rxjs";
import { map, catchError, switchMap, tap, finalize } from "rxjs/operators";
import { environment } from "@environment/environment";
import {
  IQuestionnaire,
  IQuestionnaireContent,
} from "@app/shared/models/questionnaire";
import { Client, ClientSite } from "@app/shared/models/client";
import { sector } from "@app/shared/models/sector";
import { AnswerSet, IAnswerSet } from "@app/shared/models/answerset";
import { IPeninsulaDocument, PeninsulaDocument } from "@shared/models/peninsulaDocument";
import { Notification } from "../components/notification/models/notification.model";
import { NotificationService } from "@app/shared/components/notification/notification.service";
import { AppEventType, AppEvent, BroadcastService } from "./broadcast.service";
import { AnswerSetList } from "../models/answerset-list";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { LocalStorageService } from "./local-storage-service";
import { SKIP_LOADER_HEADER } from "../components/loader/loader-interceptor.service";
import { IGenerateDocumentResponse, IGenerateDocumentStatusResponse } from "@shared/models/peninsulaDocument";

export class QuestionnaireFileType {
  Questionnaire: any = { body: [] };
  Model = { body: [] };
}
@Injectable({
  providedIn: "root",
})
export class QuestionnaireService implements OnDestroy {
  private cachedQuestionnaire: QuestionnaireFileType;
  private cachedSectors: sector[];
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private broadcastService: BroadcastService,
    private _localStorageService: LocalStorageService,
  ) {
      this.getSectors().subscribe(d => {
        this.cachedSectors = d;
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  // GET DEPARTMENT SET IN LOCALSTORAGE
  getDepartmentFromLocalStorage(): string | null {
    return this._localStorageService.getItem('selectedDepartment');
  }

  // GET SECTORS
  getSectors(): Observable<sector[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuildersectors/Sectors?department=${department}`;

    return this.http.get(url).pipe(
      map((response: sector[]) => {
        this.cachedSectors = response;

        return this.cachedSectors;
      })
    );
  }

  // GET SECTOR NAME(S) BY ID
  getSector(id: string): string {
    if (!this.cachedSectors) {
      console.log("Sectors are empty");
      return "";
    }

    const sectorNames = id
      .split(";")
      .filter((sectorId) => sectorId.trim().length > 10)
      .map((sectorId) => {
        const sector = this.cachedSectors.find(
          (s) => s.id.toString().trim() === sectorId.trim()
        );

        if (!sector) {
          console.log(`No SECTOR FOR ${sectorId}`);
          return null;
        }

        return sector.sectorName;
      })
      .filter((name) => name !== null);

    return sectorNames.join(", ");
  }

  // GET SECTOR AS OBJECT BY ID
  getSectorAsObject(id: string): sector[] {
    if (!this.cachedSectors) {
      console.log("Sectors are empty");

      return [];
    }

    return id
      .split(";")
      .filter((sectorId) => sectorId.trim().length > 10)
      .map((sectorId) =>
        this.cachedSectors.find(
          (s) => s.id.toString().trim() === sectorId.trim()
        )
      )
      .filter((sector) => sector !== undefined);
  }

  // GET CURRENT QUESTIONNAIRE
  getCurrentQuestionnaire(): QuestionnaireFileType {
    return this.cachedQuestionnaire;
  }

  // GET CLIENTS BY CAN
  getClients(can: string): Observable<Client[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/departments/${department}/clients/${can}/bycan`;

    return this.http.get(url).pipe(
      map((response: Client[]) => {
        return response.map(
          (item: Client) =>
            ({
              clientId: item.id,
              clientFullName: item.clientName,
              clientName: item.clientName,
              can: item.can,
            } as Client)
        );
      })
    );
  }

  // GET CLIENT BY CLIENT ID
  getClient(clientId: string): Observable<Client> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/departments/${department}/clients/${clientId}`;

    return this.http.get(url).pipe(
      map((response: Client) => {
        const c = new Client();

        c.clientId = response.id;
        c.clientFullName = response.clientName;
        c.clientName = response.clientName;
        c.can = response.can;

        if (!c.clientFullName) c.clientFullName = "Not found";

        return c;
      })
    );
  }

  // GET RECENT CLIENTS BY DEPARTMENT
  getRecentClients(): Observable<Client[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/departments/${department}/GetRecentClients`;

    return this.http.get(url).pipe(
      map((recentClients: Client[]) => {
        if (recentClients.length > 0) {
          return recentClients;
        }
      })
    );
  }

  // GET CLIENT SITES BY CLIENT ID
  getClientSitesByClientId(clientId: string): Observable<ClientSite[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/departments/${department}/clients/${clientId}/siteaddresses`;

    return this.http.get<ClientSite[]>(url);
  }

  // GET ANSWER SETS BY CLIENT ID
  getAnswerSets(clientId: string): Observable<AnswerSetList[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderanswersets/clients/${clientId}?department=${department}`;

    return this.http.get(url).pipe(
      map((answerSet: AnswerSetList[]) => {
        return answerSet;
      })
    );
  }

  // GET QUESTIONNAIRES LIST
  getQuestionnaires(): Observable<IQuestionnaire[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderquestionnaire?department=${department}`;

    return this.http.get<any[]>(url).pipe(
      map((res) =>
        res.map(
          (item) =>
            ({
              questionnaireName: item.title,
              dateCreated: this.getFormattedDate(item.createdDate),
              id: item.id,
              createdBy: item.createdBy,
              modifiedBy: item.modifiedBy,
              dateModified: this.getFormattedDate(item.modifiedDate),
              questionnaireSector: "Not Required",
            } as IQuestionnaire)
        )
      ),
      catchError(this.handleError)
    );
  }

  // GET QUESTIONNAIRE CONTENT AND MODEL
  getQuestionnaireContent(id: string): Observable<any> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderquestionnaire/${id}/get?department=${department}`;

    return this.http.get(url).pipe(
      map((response: IQuestionnaireContent) => {
        const sanitizedQuestionnaireJson = this.sanitizeHideExpressions(
          JSON.parse(response.questionnaireJson).Questionnaire.body
        );

        response.questionnaireJson = JSON.stringify({
          ...JSON.parse(response.questionnaireJson),
          Questionnaire: { body: sanitizedQuestionnaireJson },
        });

        if (response.metadata != null && response.metadata != undefined) {
          const parsedQuestionnaire = JSON.parse(response.questionnaireJson);
          this.cachedQuestionnaire = new QuestionnaireFileType();
          this.cachedQuestionnaire = parsedQuestionnaire;

          const Model = parsedQuestionnaire.Model.body[0];
          const Questionnaire = parsedQuestionnaire.Questionnaire;

          return [Model, Questionnaire];
        }
      })
    );
  }

  // GET QUESTIONNAIRE BY ID
  getQuestionnaireById(id: string): Observable<IQuestionnaireContent> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderquestionnaire/${id}/get?department=${department}`;

    return this.http.get(url).pipe(
      map((response: IQuestionnaireContent) => {
        const sanitizedQuestionnaireJson = this.sanitizeHideExpressions(
          JSON.parse(response.questionnaireJson).Questionnaire.body
        );

        response.questionnaireJson = JSON.stringify({
          ...JSON.parse(response.questionnaireJson),
          Questionnaire: { body: sanitizedQuestionnaireJson },
        });

        return response;
      })
    );
  }

  // GET ANSWER SET CONTENT AND MODEL / GET ANSWER SET BY ID
  getAnswerSetContent(id: string): Observable<IAnswerSet> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderanswersets/${id}/Get?department=${department}`;

    return this.http.get(url).pipe(
      map((response: IAnswerSet) => {
        return response;
      })
    );
  }

  // GET ANSWER SET DATA AND QUESTIONNAIRE DATA BY ANSWER SET ID
  getAnswerSetData(id: string): Observable<any> {
    return this.getAnswerSetContent(id).pipe(
      switchMap((answerSetData: IAnswerSet) => {
        return this.getQuestionnaireById(
          answerSetData.metadata.questionnaireId.toString()
        ).pipe(
          map((questionnaireData: IQuestionnaireContent) => {
            const answerSet = {
              id: answerSetData.metadata.id,
              answerSetName: answerSetData.metadata.title,
              clientId: answerSetData.metadata.clientId,
              clientSideId: answerSetData.metadata.siteId,
              questionnaireId:
                answerSetData.metadata.questionnaireId.toString(),
              created: answerSetData.metadata.createdDate,
              createdBy: answerSetData.metadata.createdBy,
              body: JSON.parse(answerSetData.answerSetJson),
            };

            const existingFields = {
              id: questionnaireData.metadata.id,
              questionnaireName: questionnaireData.metadata.title,
              dateCreated: questionnaireData.metadata.createdDate,
              createdBy: questionnaireData.metadata.createdBy,
              body: JSON.parse(questionnaireData.questionnaireJson)
                .Questionnaire.body,
            };

            return [answerSet, existingFields];
          })
        );
      })
    );
  }

  // ADD NEW ANSWER SET
  addAnswerSet(newAnswerSet: AnswerSet, clientId: string): Observable<any> {
    const department = this.getDepartmentFromLocalStorage();

    return this.getQuestionnaireContent(newAnswerSet.questionnaireId).pipe(
      switchMap(([existingModel, existingFields]) => {
        if (existingModel.hideExpressionValue) {
          existingModel.hideExpressionValue =
            newAnswerSet.sectors.map((sector) => sector.id).join(";") + ";";
        }

        newAnswerSet.body = existingModel;

        const body = JSON.stringify(newAnswerSet.body);

        const url = `${environment.api}/api/v1/docbuilderanswersets/create?QuestionnaireId=${newAnswerSet.questionnaireId}&ClientId=${clientId}&Department=${department}&SectorId=${newAnswerSet.sectors[0].id}&SiteId=${newAnswerSet.clientSiteId}&Title=${newAnswerSet.answerSetName}`;

        return this.http.post(url, body).pipe(
          map((response) => {
            if (response) {
              this.notificationService.notification(
                new Notification("success", "Message:", "Created!")
              );
            }
          })
        );
      })
    );
  }

  // ADD NEW QUESTIONNAIRE
  addQuestionnaires(questionnaireName: string): Observable<any> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderquestionnaire/create?Department=${department}&Title=${questionnaireName}`;

    const newQWithModel =
      '{"Questionnaire":{ "body": [ ] }, "Model": {"body": [{"hideExpressionValue": ""}]}}';

    const body = newQWithModel;

    return this.http.post(url, body).pipe(
      map((response: any) => {
        if (response) {
          this.notificationService.notification(
            new Notification("success", "Message:", "Created!")
          );
        }

        return response;
      })
    );
  }

  // CLONE EXISTING QUESTIONNAIRE
  cloneQuestionnaires(
    questionnaireName: string,
    questionnaireId: string
  ): Observable<any> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderquestionnaire/clone`;

    const body = {
      sourceId: questionnaireId.toString(),
      newTitle: questionnaireName,
      department: department,
      description: questionnaireName,
    };

    return this.http.post(url, body).pipe(
      map((response: any) => {
        if (response) {
          this.notificationService.notification(
            new Notification("success", "Message:", "Cloned!")
          );
        }
        return response;
      })
    );
  }

  // GET DRAFT DOCUMENTS BY QUESTIONNAIRE ID
  getDraftDocuments(questionnaireId: string): Observable<PeninsulaDocument[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/draftdocuments/questionnaires/${questionnaireId}?department=${department}`;

    return this.http.get(url).pipe(
      map((documents: PeninsulaDocument[]) => {
        const peninsulaDocs: PeninsulaDocument[] = documents
          .map((item: IPeninsulaDocument) => {
            const doc = new PeninsulaDocument();
            doc.title = item.title;
            doc.id = item.id;
            doc.createdBy = item.createdBy;
            doc.fileUrl = item.fileUrl;
            doc.createdDate = this.getFormattedDate(item.createdDate);
            doc.modifiedBy = item.modifiedBy;
            doc.modifiedDate = this.getFormattedDate(item.modifiedDate);
            doc.version = item.version;
            doc.status = doc.version
              ? this.documentStatus(Number(doc.version))
              : "working";
            return doc;
          })
          .filter((doc: PeninsulaDocument) => doc.status !== "Draft");

        return peninsulaDocs;
      })
    );
  }

  // ADD DRAFT DOCUMENT
  addDraftDocument(title: string, questionnaireId: string): Observable<any> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/draftdocuments/create?questionnaireId=${questionnaireId}&title=${title}&department=${department}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response) {
          this.notificationService.notification(
            new Notification("success", "Message:", "Created!")
          );
        }
        return response;
      }),
      catchError(this.handleError)
    );
  }

  // ADD NEW QUESTIONNAIRE SECTION/TAB
  saveQuestionSet(fields: string, model: string, id: string): Observable<any> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/docbuilderquestionnaire/${id}/update?Department=${department}`;

    const QWithModel = new QuestionnaireFileType();

    QWithModel.Model.body[0] = model;
    QWithModel.Questionnaire = fields;

    this.cachedQuestionnaire = QWithModel;

    const body = JSON.stringify(QWithModel);

    return this.http.put(url, body).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(this.handleError)
    )
  }

  // SAVE QUESTIONNAIRE
  saveQuestionnaire(json: string, id: string, silent = false) {
    const department = this.getDepartmentFromLocalStorage();

    this.getQuestionnaireContent(id).pipe(
      switchMap(([existingModel, existingFields]) => {
        const qWithModel = new QuestionnaireFileType();

        qWithModel.Model.body[0] = json;
        qWithModel.Questionnaire = existingFields;

        this.cachedQuestionnaire = qWithModel;

        const body = JSON.stringify(qWithModel);

        const url = `${environment.api}/api/v1/docbuilderquestionnaire/${id}/update?Department=${department}`;

        return this.http.put<any>(url, body).pipe(
          map(response => {
            if (!silent) {
              this.notificationService.notification(
                new Notification('success', 'Message:', 'Saved!')
              );
            }
          })
        );
      }),
      catchError(this.handleError)
    ).subscribe();
  }

  // SAVE ANSWER SET
  saveAnswerSet(json: any, id: string, title: string, silent: boolean = true) {
    this.saveAnswerSet$(json, id, title, silent).subscribe(
      () => {},
      (error) => {
        console.log(error);
      }
    );
  }

  // Silent saves run in the background: no full-screen loader and no "Saved!" toast,
  // so the user can keep typing while the save is in flight.
  saveAnswerSet$(json: any, id: string, title: string, silent: boolean = true): Observable<any> {
    const department = encodeURIComponent(this.getDepartmentFromLocalStorage());

    const url = `${environment.api}/api/v1/docbuilderanswersets/${encodeURIComponent(id)}/update?Department=${department}&Title=${encodeURIComponent(title ?? '')}`;

    // serialise now so edits made while the request is in flight aren't mixed into this save
    const body = JSON.stringify(json);

    const headers = silent ? new HttpHeaders({ [SKIP_LOADER_HEADER]: 'true' }) : undefined;

    //broadcast an event to indicate that data fetching began
    this.broadcastService.dispatch(
      new AppEvent(AppEventType.DataFetchingStarted)
    );

    return this.http.put(url, body, { headers }).pipe(
      tap(() => {
        if (!silent)
          this.notificationService.notification(
            new Notification("success", "Message:", "Saved!")
          );
      }),
      finalize(() => {
        //broadcast an event to indicate that data fetching completed
        this.broadcastService.dispatch(
          new AppEvent(AppEventType.DataFetchingCompleted)
        );
      })
    );
  }

  // GET GENERATED DOCUMENTS
  getGeneratedDocuments(
    answerSetId: string,
    clientId: string
  ): Observable<PeninsulaDocument[]> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/generatedocuments/clients/${clientId}/Get?answerSetId=${answerSetId}&department=${department}`;

    return this.http.get<IPeninsulaDocument[]>(url).pipe(
      map((response: IPeninsulaDocument[]) => {
        const results = response ?? [];

        const peninsulaDocs = results.map((item: IPeninsulaDocument) => {
          const doc = new PeninsulaDocument();
          doc.title = item.title;
          doc.id = item.id;
          doc.createdBy = item.createdBy;
          doc.createdDate = item.createdDate;
          doc.modifiedBy = item.modifiedBy;
          doc.modifiedDate = item.modifiedDate;
          doc.version = item.version;
          doc.status = doc.version
            ? this.documentStatus(Number(doc.version))
            : "working";
          doc.fileUrl = item.fileUrl;
          return doc;
        });

        return peninsulaDocs;
      })
    );
  }

  // GENERATE BESPOKE DOCUMENTS
  createGenerateDocuments(
    draftDocumentIds: string[],
    answerSetId: string,
    clientId: string,
    can: string
  ): Observable<IGenerateDocumentResponse> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/generatedocuments/clients/${clientId}/Generate?answerSetId=${answerSetId}&department=${department}&can=${can}`;

    return this.http.post<IGenerateDocumentResponse>(url, draftDocumentIds);
  }

  // GENERATE DOCUMENT STATUS
  generateDocumentStatus(instanceId: string): Observable<IGenerateDocumentStatusResponse> {
    if (!instanceId || instanceId.trim() === "") {
      return;
    }

    const headers = new HttpHeaders({'Skip-Loader': 'true'});

    const url = `${environment.api}/api/v1/generatedocuments/status/${instanceId}`;

    return this.http.get<IGenerateDocumentStatusResponse>(url, { headers });
  }

  // PUBLISH GENERATED DOCUMENTS
  PublishGeneratedDocuments(idsToPublish: string[], clientId: string): Observable<string> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/generatedocuments/clients/${clientId}/Publish?department=${department}`;

    return this.http.post<string>(url, idsToPublish);
  }

  // DELETE GENERATED DOCUMENTS
  DeleteGeneratedDocuments(idsToDelete: string[], clientId: string): Observable<string> {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/generatedocuments/clients/${clientId}/Delete?department=${department}`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: idsToDelete
    };

    return this.http.delete<string>(url, httpOptions);
  }

  // EDIT GENERATED DOCUMENT NAME
  editGeneratedDocumentName(clientId: string, document: IPeninsulaDocument): void {
    const department = this.getDepartmentFromLocalStorage();

    const url = `${environment.api}/api/v1/generatedocuments/${document.id}/Update?clientId=${clientId}&title=${document.title}&department=${department}`;

    this.http.post(url, {}).pipe(
      map(() => {
        this.notificationService.notification(
          new Notification("success", "Message:", "Document name changed")
        );
      })
    ).subscribe();
  }

  // HELPER METHOD
  sanitizeHideExpressions(tabs: any) {
    try {
      if (Array.isArray(tabs)) {
        tabs.forEach((tab) => {
          if (Array.isArray(tab.fields)) {
            tab.fields.forEach((f) => this.migrateHideExpression(f));
          }
        });
        return tabs;
      }
    } catch (e) {
      console.warn("[sanitizeHideExpressions] failed", e);
    }
  }

  // HELPER METHOD
  migrateHideExpression(field: FormlyFieldConfig) {
    if (!field) return;

    if (
      typeof field.hideExpression === "string" &&
      field.hideExpression.indexOf("this.field") >= 0
    ) {
      field.hideExpression =
        '!((model.hideExpressionValue || "").split(";").some(e => e && field.sector && field.sector.indexOf(e) >= 0)) && ((model.hideExpressionValue || "").indexOf(field.id) < 0)';
    }

    if (field.fieldGroup && field.fieldGroup.length) {
      field.fieldGroup.forEach((child) => this.migrateHideExpression(child));
    }
  }

  // HELPER METHOD
  private handleError(error: HttpErrorResponse): Observable<never> {
    const isClientError = error.error instanceof ErrorEvent;
    const errorMessage = isClientError ? error.error.message : error.message;
    const notificationTitle = isClientError
      ? "Error:"
      : `Error: ${error.status}`;

    this.notificationService.notification(
      new Notification("danger", notificationTitle, errorMessage)
    );

    console.error(
      isClientError
        ? `Error: ${errorMessage}`
        : `Error Code: ${error.status}\nError Message: ${errorMessage}`
    );

    return throwError(
      () => new Error("Something bad happened; please try again later.")
    );
  }

  // HELPER METHOD
  private documentStatus(version: number): string {
    return version % 1 !== 0 ? "Draft" : "Published";
  }

  // HELPER METHOD
  private getFormattedDate(dateString: string): string {
    if (!dateString?.trim()) {
      return dateString;
    }

    const date = new Date(dateString);

    return isNaN(date.getTime()) ? dateString : date.toLocaleString();
  }

  //----------------------------------------------------------------- OLD CRAP ---------------------------------------------------------------
}
